import React, { useState } from "react";
import { IoAlertCircle, IoCheckmarkCircle, IoRefreshOutline, IoTimeOutline } from "react-icons/io5";

// Maps the backend task_status enum to a friendly label + color "tone".
const STATUS_META = {
  pending: { label: "Queued", tone: "slate" },
  processing: { label: "Processing", tone: "blue" },
  db_processing: { label: "Saving data", tone: "indigo" },
  reading_file: { label: "Reading file", tone: "cyan" },
  scraping_url: { label: "Scraping website", tone: "sky" },
  formatting_scraped_data: { label: "Formatting data", tone: "purple" },
  formatting_file_data: { label: "Formatting data", tone: "purple" },
  completed: { label: "Up to date", tone: "green" },
  failed: { label: "Failed", tone: "red" },
};

// Keep text/icons readable while a softer fill layer grows behind them.
const TONE_STYLES = {
  slate: {
    className: "border-slate-200 bg-slate-50 text-slate-700",
    fill: "rgba(100, 116, 139, 0.22)",
  },
  blue: {
    className: "border-blue-100 bg-blue-50 text-blue-700",
    fill: "rgba(59, 130, 246, 0.22)",
  },
  indigo: {
    className: "border-indigo-100 bg-indigo-50 text-indigo-700",
    fill: "rgba(99, 102, 241, 0.22)",
  },
  cyan: {
    className: "border-cyan-100 bg-cyan-50 text-cyan-700",
    fill: "rgba(6, 182, 212, 0.22)",
  },
  sky: {
    className: "border-sky-100 bg-sky-50 text-sky-700",
    fill: "rgba(14, 165, 233, 0.22)",
  },
  purple: {
    className: "border-purple-100 bg-purple-50 text-purple-700",
    fill: "rgba(147, 51, 234, 0.22)",
  },
  green: {
    className: "border-green-100 bg-green-50 text-green-700",
    fill: "rgba(34, 197, 94, 0.26)",
  },
  red: {
    className: "border-red-100 bg-red-50 text-red-700",
    fill: "rgba(239, 68, 68, 0.2)",
  },
};

const parseProgressPercent = (progress) => {
  if (progress === null || progress === undefined) return null;
  const match = String(progress).match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isNaN(value) ? null : Math.min(100, Math.max(0, value));
};

// Injected directly (rather than relying on a Tailwind config keyframe) so
// the sliding "indeterminate" animation always works, even if the app's
// Tailwind build hasn't picked up a config change / isn't running.
const INDETERMINATE_KEYFRAMES = `
@keyframes plist-knowledge-progress-sweep {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(220%); }
}
`;

/**
 * Live status chip for the knowledge-ingestion pipeline. The pill background
 * itself acts as the progress bar while text/icons remain visible above it.
 */
const KnowledgeStatusPill = ({ status, progress, isRefreshing, isRetrying, onRefresh }) => {
  const [isManualSpinning, setIsManualSpinning] = useState(false);

  if (!status) return null;

  const meta = STATUS_META[status] || { label: status, tone: "slate" };
  const isCompleted = status === "completed";
  const isFailed = status === "failed";
  const isPending = status === "pending";
  const isTerminal = isCompleted || isFailed;
  const rawPercent = parseProgressPercent(progress);
  const toneStyle = TONE_STYLES[meta.tone] || TONE_STYLES.slate;
  const progressPercent = isCompleted ? 100 : isFailed ? 100 : rawPercent ?? (isPending ? 10 : 18);
  const showIndeterminateSweep = !isTerminal && rawPercent == null;
  const showPercentText = !isTerminal && rawPercent != null;
  const showRetrySpinner = isFailed && isRetrying;

  const handleManualRefresh = (event) => {
    event.stopPropagation();
    if (!onRefresh) return;
    setIsManualSpinning(true);
    onRefresh();
    window.setTimeout(() => setIsManualSpinning(false), 700);
  };

  const tooltip =
    !isTerminal && rawPercent != null ? `${meta.label} – ${Math.round(rawPercent)}%` : meta.label;

  return (
    <>
      <style>{INDETERMINATE_KEYFRAMES}</style>
      <div
        role="status"
        aria-live="polite"
        title={tooltip}
        className={`relative inline-flex shrink-0 items-center gap-2 overflow-hidden whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors duration-300 ${
          toneStyle.className
        } ${showRetrySpinner ? "cursor-wait opacity-80" : ""}`}
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
          style={{
            width: `${progressPercent}%`,
            minWidth: progressPercent > 0 ? "18px" : 0,
            backgroundColor: toneStyle.fill,
          }}
        />
        {showIndeterminateSweep && (
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-white/30"
            style={{ animation: "plist-knowledge-progress-sweep 1.4s ease-in-out infinite" }}
          />
        )}

        <span className="relative z-10 flex h-4 w-4 shrink-0 items-center justify-center">
          {showRetrySpinner ? (
            <IoRefreshOutline className="h-4 w-4 animate-spin" />
          ) : isCompleted ? (
            <IoCheckmarkCircle className="h-4 w-4" />
          ) : isFailed ? (
            <IoAlertCircle className="h-4 w-4" />
          ) : isPending ? (
            <IoTimeOutline className="h-4 w-4" />
          ) : (
            <span className="relative flex h-2.5 w-2.5">
              <span className="bg-current absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
              <span className="bg-current relative inline-flex h-2.5 w-2.5 rounded-full" />
            </span>
          )}
        </span>

        <span className="relative z-10">{showRetrySpinner ? "Retrying…" : meta.label}</span>
        {showPercentText && (
          <span className="relative z-10 text-[11px] font-bold opacity-75">
            {Math.round(rawPercent)}%
          </span>
        )}

        {onRefresh && !isTerminal && (
          <button
            type="button"
            onClick={handleManualRefresh}
            className="relative z-10 -mr-1 ml-0.5 rounded-full p-0.5 opacity-60 transition-opacity hover:opacity-100"
            aria-label="Refresh status now"
            title="Refresh status now"
          >
            <IoRefreshOutline
              className={`h-3 w-3 ${isRefreshing || isManualSpinning ? "animate-spin" : ""}`}
            />
          </button>
        )}
      </div>
    </>
  );
};

export default KnowledgeStatusPill;
